import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, AlertTriangle, AlertOctagon, Info, RefreshCw,
  HelpCircle, ChevronRight,
} from "lucide-react";
import ReadinessDonut from "@/components/ReadinessDonut";

// Automation Readiness Report (full card).
// Strictly read-only. Renders the 14-section breakdown grouped into
// Required / Recommended / Optional with status icons, progress bars, and
// deep-link buttons to the relevant Setup pages.

const STATUS_ICON = {
  ok: { Icon: CheckCircle2, tone: "text-emerald-600" },
  warn: { Icon: AlertTriangle, tone: "text-amber-600" },
  critical: { Icon: AlertOctagon, tone: "text-red-600" },
  info: { Icon: Info, tone: "text-[#2563EB]" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

function ProgressBar({ value, status }) {
  const pct = Math.round((value || 0) * 100);
  const fill = status === "critical" ? "bg-red-500"
             : status === "warn" ? "bg-amber-500"
             : status === "info" ? "bg-[#2563EB]"
             : "bg-emerald-500";
  return (
    <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full ${fill}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function SectionRow({ s }) {
  const { Icon, tone } = STATUS_ICON[s.status] || STATUS_ICON.info;
  const [expanded, setExpanded] = useState(false);
  const affected = Array.isArray(s.affected) ? s.affected : [];
  const hasAffected = affected.length > 0;
  return (
    <li className="border-b border-slate-100 last:border-0"
        data-testid={`readiness-row-${s.key}`}>
      <div className="flex items-center gap-3 py-2">
        <Icon className={`h-4 w-4 shrink-0 ${tone}`}
                data-testid={`readiness-row-status-${s.key}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#0B1A36] truncate">
              {s.title}
            </span>
            {s.not_applicable && (
              <span className="text-[10px] uppercase tracking-widest text-[#4B5773] px-1.5 py-0.5 rounded bg-slate-100">
                N/A
              </span>
            )}
          </div>
          <div className="text-xs text-[#4B5773] truncate">{s.headline}</div>
          {hasAffected && (
            <button type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[#2563EB] hover:underline"
                    data-testid={`readiness-row-toggle-affected-${s.key}`}>
              {expanded ? "Hide" : `View the ${affected.length} item${affected.length === 1 ? "" : "s"} that need attention`}
              <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>
          )}
        </div>
        <ProgressBar value={s.score} status={s.status} />
        {s.action?.href && (
          <Link to={s.action.href}
                data-testid={`readiness-row-action-${s.key}`}
                className="text-xs text-[#2563EB] hover:underline inline-flex items-center shrink-0">
            {s.action.label || "Open"}
            <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      {hasAffected && expanded && (
        <ul className="ml-7 mb-2 pl-2 border-l border-slate-200 space-y-1"
            data-testid={`readiness-row-affected-${s.key}`}>
          {affected.map((a) => (
            <li key={a.id || a.name}
                className="flex items-center justify-between gap-3 text-xs py-1"
                data-testid={`readiness-row-affected-item-${s.key}-${a.id || a.name}`}>
              <span className="text-[#4B5773] truncate">
                <span className="text-[10px] uppercase tracking-widest text-[#94A3B8] mr-1.5">
                  {a.kind || "item"}
                </span>
                {a.name}
              </span>
              {a.fix_href && (
                <Link to={a.fix_href}
                      className="text-[#2563EB] hover:underline inline-flex items-center gap-0.5 shrink-0"
                      data-testid={`readiness-row-affected-fix-${s.key}-${a.id || a.name}`}>
                  Fix
                  <ChevronRight className="h-3 w-3" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function ReadinessReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/automation/readiness");
      setData(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <Card title="Setup health">
        <div className="text-sm text-[#4B5773] py-6 text-center">Loading…</div>
      </Card>
    );
  }

  const grouped = {};
  for (const s of data.sections) {
    if (onlyIssues && s.status === "ok") continue;
    (grouped[s.group] ||= []).push(s);
  }

  return (
    <Card title={null}>
      <div data-testid="readiness-report">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <ReadinessDonut value={data.overall_score}
                             testid="readiness-overall" />
            <div>
              <h2 className="text-xl font-semibold text-[#0B1A36] tracking-tight">
                Setup health
              </h2>
              <div className="text-sm text-[#4B5773]"
                    data-testid="readiness-band">
                {data.headline}
              </div>
              <p className="text-[11px] text-[#4B5773] mt-1 max-w-2xl">
                How ready your setup is for advisory tips and (later) automation. Nothing on this
                page changes how your team operates today.
              </p>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-[#4B5773]">
                <span data-testid="readiness-summary">
                  {data.summary.ok} healthy · {data.summary.warn} could be better
                  · {data.summary.critical} need attention · {data.summary.info} tips
                </span>
                <span>Last checked {timeAgo(data.computed_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-[#4B5773] cursor-pointer">
              <input type="checkbox"
                     checked={onlyIssues}
                     onChange={(e) => setOnlyIssues(e.target.checked)}
                     className="accent-[#2563EB]"
                     data-testid="readiness-show-only-issues" />
              Show only items needing attention
            </label>
            <Button variant="outline" size="sm" onClick={load}
                    disabled={busy} data-testid="readiness-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <button type="button" onClick={() => setHelpOpen((v) => !v)}
                    data-testid="readiness-help"
                    className="text-[#4B5773] hover:text-[#0B1A36]"
                    aria-label="What does this measure?">
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
        </div>

        {helpOpen && (
          <p data-testid="readiness-help-text"
              className="mt-3 p-3 rounded-md bg-[#F6F9FE] border border-slate-200 text-xs text-[#4B5773]">
            {data.what_it_measures}
          </p>
        )}

        <div className="mt-5 space-y-5">
          {Object.entries(data.groups).map(([gKey, gLabel]) => {
            const rows = grouped[gKey] || [];
            if (!rows.length) return null;
            const totalWeight = rows.reduce((s, r) => s + r.weight, 0);
            return (
              <div key={gKey} data-testid={`readiness-group-${gKey}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-[11px] uppercase tracking-widest font-medium text-[#0B1A36]">
                    {gLabel}
                  </h3>
                  <span className="text-[11px] text-[#4B5773]">
                    {totalWeight} pts
                  </span>
                </div>
                <ul className="border border-slate-200 rounded-lg px-3">
                  {rows.map((s) => <SectionRow key={s.key} s={s} />)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
